import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users, ArrowLeft, Search, Shield, Loader2, ShieldCheck, Globe, ExternalLink, UserPlus, KeyRound, Ban, Play, Trash2, X, MoreHorizontal } from 'lucide-react';
import { adminListUsers, adminCreateUser, adminDeleteUser, adminResetPassword, adminBlockUser, adminUnblockUser } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function AdminUsers() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [searchTimer, setSearchTimer] = useState(null);
  const [modal, setModal] = useState(null); // { type: 'create'|'reset'|'delete', user }

  const load = async (search) => {
    try { setItems(await adminListUsers(search)); } catch { setItems([]); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) { nav('/'); return; }
    load('');
  }, [user, authLoading, nav]);

  const onSearch = (v) => {
    setQ(v);
    if (searchTimer) clearTimeout(searchTimer);
    setSearchTimer(setTimeout(() => load(v), 350));
  };

  const doBlock = async (u) => {
    try {
      if (u.blocked) { await adminUnblockUser(u.id); toast.success('Utente sbloccato'); }
      else { await adminBlockUser(u.id); toast.success('Utente bloccato'); }
      load(q);
    } catch (e) { toast.error('Errore', { description: e?.response?.data?.detail || '' }); }
  };

  return (
    <div className="py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-700 flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Admin</div>
            <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-2"><Users className="h-8 w-8 text-[#0d63b0]" /> Utenti</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setModal({ type: 'create' })} className="bg-[#0d63b0] hover:bg-[#0a4f8f] text-white"><UserPlus className="h-4 w-4 mr-1.5" /> Nuovo utente</Button>
            <Button asChild variant="outline"><Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1.5" /> Torna al pannello</Link></Button>
          </div>
        </div>

        <Card className="border border-slate-200 p-5 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => onSearch(e.target.value)} placeholder="Cerca per email..." className="pl-10" />
          </div>
        </Card>

        <Card className="border border-slate-200 p-0 overflow-hidden">
          {items === null && <div className="p-8 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>}
          {items && items.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">Nessun utente trovato.</div>}
          {items && items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    <th className="px-5 py-3">Email</th>
                    <th>Brand</th>
                    <th>Piano</th>
                    <th>Stato</th>
                    <th>Registrato</th>
                    <th className="pr-5 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((u) => (
                    <tr key={u.id} className={`border-b border-slate-100 hover:bg-slate-50/50 ${u.blocked ? 'bg-red-50/30' : ''}`}>
                      <td className="px-5 py-3">
                        <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                          {u.email}
                          {u.is_admin && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 text-[10px] font-bold"><Shield className="h-2.5 w-2.5" /> ADMIN</span>}
                        </div>
                      </td>
                      <td>
                        {u.brand_name ? (
                          <div>
                            <div className="font-medium text-slate-800">{u.brand_name}</div>
                            <div className="text-[11px] font-mono text-slate-500">/b/{u.brand_slug}</div>
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td>
                        {u.plan && <span className="inline-flex rounded-full bg-blue-50 text-[#0d63b0] border border-blue-100 px-2 py-0.5 text-xs font-semibold capitalize">{u.plan}</span>}
                      </td>
                      <td>
                        {u.blocked ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-xs font-semibold"><Ban className="h-3 w-3" /> Bloccato</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-semibold">Attivo</span>
                        )}
                      </td>
                      <td className="text-slate-500 text-xs">{new Date(u.created_at).toLocaleDateString('it-IT')}</td>
                      <td className="pr-5 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="outline" title="Reset password" onClick={() => setModal({ type: 'reset', user: u })}><KeyRound className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="outline" title={u.blocked ? 'Sblocca' : 'Blocca'} onClick={() => doBlock(u)} disabled={u.id === user.id}>
                            {u.blocked ? <Play className="h-3.5 w-3.5 text-emerald-600" /> : <Ban className="h-3.5 w-3.5 text-amber-600" />}
                          </Button>
                          <Button size="sm" variant="outline" title="Elimina" onClick={() => setModal({ type: 'delete', user: u })} disabled={u.id === user.id} className="text-red-600 border-red-200 hover:border-red-300 hover:text-red-700">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          {u.brand_slug && (
                            <Link to={`/b/${u.brand_slug}`} target="_blank" className="ml-1 p-2 text-slate-400 hover:text-[#0d63b0]" title="Visita brand"><ExternalLink className="h-3.5 w-3.5" /></Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {items && items.length > 0 && <div className="mt-3 text-xs text-slate-500 text-right">{items.length} utenti visualizzati</div>}
      </div>

      {modal?.type === 'create' && <CreateUserModal onClose={() => setModal(null)} onDone={() => { setModal(null); load(q); }} />}
      {modal?.type === 'reset' && <ResetPasswordModal user={modal.user} onClose={() => setModal(null)} />}
      {modal?.type === 'delete' && <DeleteUserModal user={modal.user} onClose={() => setModal(null)} onDone={() => { setModal(null); load(q); }} />}
    </div>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-md w-full p-6 border border-slate-200 bg-white relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        <h2 className="text-lg font-bold text-slate-900 mb-4">{title}</h2>
        {children}
      </Card>
    </div>
  );
}

function CreateUserModal({ onClose, onDone }) {
  const [form, setForm] = useState({ email: '', password: '', brand_name: '', plan: 'free', is_admin: false });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error('Password troppo corta');
    setLoading(true);
    try { await adminCreateUser(form); toast.success('Utente creato'); onDone(); }
    catch (err) {
      const d = err?.response?.data?.detail || 'Errore';
      if (d === 'email_already_registered') toast.error('Email già registrata');
      else toast.error('Errore', { description: d });
    } finally { setLoading(false); }
  };
  return (
    <ModalShell title="Crea nuovo utente" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="user@example.com" className="mt-1" /></div>
        <div><Label>Password iniziale</Label><Input type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 caratteri" className="mt-1 font-mono" /></div>
        <div><Label>Nome brand</Label><Input required value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} placeholder="Es. Acme IT" className="mt-1" /></div>
        <div>
          <Label>Piano iniziale</Label>
          <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
            <option value="free">Free (€0 · 50MB)</option>
            <option value="starter">Starter (€5 · 100MB)</option>
            <option value="pro">Pro (€20 · 500MB)</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 pt-1"><input type="checkbox" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} /> Rendi amministratore</label>
        <div className="flex gap-2 pt-3">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Annulla</Button>
          <Button type="submit" disabled={loading} className="flex-1 bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><UserPlus className="h-4 w-4 mr-1.5" /> Crea</>)}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const [pw, setPw] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error('Password troppo corta');
    setLoading(true);
    try { await adminResetPassword(user.id, pw); toast.success('Password reimpostata', { description: `Nuova password: ${pw}` }); onClose(); }
    catch { toast.error('Errore'); } finally { setLoading(false); }
  };
  return (
    <ModalShell title={`Reset password: ${user.email}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Label>Nuova password</Label>
        <Input type="text" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Almeno 6 caratteri" className="font-mono" />
        <div className="text-xs text-slate-500">L'utente potrà accedere immediatamente con la nuova password. Comunicagliela in modo sicuro.</div>
        <div className="flex gap-2 pt-3">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Annulla</Button>
          <Button type="submit" disabled={loading} className="flex-1 bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><KeyRound className="h-4 w-4 mr-1.5" /> Reimposta</>)}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function DeleteUserModal({ user, onClose, onDone }) {
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (confirm !== user.email) return toast.error('Digita l\'email esatta per confermare');
    setLoading(true);
    try { const r = await adminDeleteUser(user.id); toast.success('Utente eliminato', { description: `${r.pushes_deleted || 0} push eliminati` }); onDone(); }
    catch (e) { toast.error('Errore', { description: e?.response?.data?.detail || '' }); } finally { setLoading(false); }
  };
  return (
    <ModalShell title="⚠️ Elimina utente" onClose={onClose}>
      <div className="rounded-lg border border-red-200 bg-red-50/60 p-4 text-sm text-red-900">
        Verranno eliminati definitivamente: <strong>{user.email}</strong>, il suo brand <strong>{user.brand_name || '—'}</strong> e tutti i suoi push. Operazione <strong>irreversibile</strong>.
      </div>
      <div className="mt-4">
        <Label>Per confermare, digita l'email:</Label>
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={user.email} className="mt-1 font-mono" />
      </div>
      <div className="flex gap-2 pt-4">
        <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Annulla</Button>
        <Button onClick={submit} disabled={loading || confirm !== user.email} className="flex-1 bg-red-600 hover:bg-red-700 text-white">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Trash2 className="h-4 w-4 mr-1.5" /> Elimina</>)}
        </Button>
      </div>
    </ModalShell>
  );
}
